'use client';

import { useEffect } from 'react';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  // Check if this is a chunk load error (common on slow connections)
  const isChunkLoadError =
    error.message?.includes('ChunkLoadError') ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.name === 'ChunkLoadError';

  useEffect(() => {
    console.error('Page error:', error);
    if (isChunkLoadError) {
      // Stale cached HTML referencing old chunk hashes: force a full reload
      // to get the current deployment's chunks. This transparently recovers
      // the user without requiring a manual click.
      window.location.reload();
    }
  }, [error, isChunkLoadError]);

  const handleRefresh = () => {
    // Force a full page reload to get fresh chunks
    window.location.reload();
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <svg
            className="w-16 h-16 mx-auto text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          {isChunkLoadError ? 'Connection Issue' : 'Something went wrong'}
        </h2>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {isChunkLoadError
            ? 'The page failed to load completely. This usually happens on slow or unstable connections.'
            : 'We encountered an unexpected error. Please try again.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center justify-center px-6 py-3 bg-[#2813e8] text-white font-medium rounded-lg hover:bg-[#1e0fb8] transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Page
          </button>

          <button
            onClick={reset}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>

        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          If this keeps happening, try{' '}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a href="/" className="text-[#2813e8] hover:underline">
            going to the homepage
          </a>{' '}
          or checking your internet connection.
        </p>
      </div>
    </div>
  );
}
